import { useEffect, useState } from 'react';
import { callListHostingReleases, type AdminHostingRelease } from '../../firebase';
import { useSort, type SortValue } from '../../sorting';
import { buildDeploymentsCsv } from '../deploymentsCsv';
import { downloadCsv, csvFilename } from '../../csv';

// Historial de releases de Firebase Hosting del site de la app web. Calca el
// patrón de UsersList: callable paginada + "Cargar más" + orden client-side.

type SortKey = 'releaseTime' | 'type' | 'releaseUser' | 'fileCount' | 'versionBytes';

function sortValue(r: AdminHostingRelease, key: SortKey): SortValue {
  switch (key) {
    case 'releaseTime':
      return r.releaseTime ? Date.parse(r.releaseTime) : null;
    case 'type':
      return r.type.toLowerCase();
    case 'releaseUser':
      return r.releaseUserEmail?.toLowerCase() ?? null;
    case 'fileCount':
      return r.fileCount;
    case 'versionBytes':
      return r.versionBytes;
  }
}

export function WebAppDeployments() {
  const [releases, setReleases] = useState<AdminHostingRelease[]>([]);
  const [pageToken, setPageToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function loadPage(token: string | null, append: boolean) {
    setLoading(true);
    setErr(null);
    try {
      const res = await callListHostingReleases({ pageToken: token ?? undefined, pageSize: 100 });
      setReleases((prev) => (append ? [...prev, ...res.data.releases] : res.data.releases));
      setPageToken(res.data.nextPageToken);
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void loadPage(null, false);
  }, []);

  const { sorted, toggle, arrow } = useSort<AdminHostingRelease, SortKey>(releases, sortValue, {
    key: 'releaseTime',
    dir: 'desc',
  });

  return (
    <div>
      <div className="row between" style={{ margin: '16px 0' }}>
        <h3 style={{ margin: 0 }}>App web ({releases.length})</h3>
        <div className="row" style={{ gap: 8 }}>
          <button
            onClick={() => downloadCsv(csvFilename('deployments-web-app'), buildDeploymentsCsv(sorted))}
            disabled={sorted.length === 0}
          >
            Exportar CSV
          </button>
          <button onClick={() => loadPage(null, false)} disabled={loading}>
            {loading ? 'Cargando…' : 'Refrescar'}
          </button>
        </div>
      </div>

      {err && <div className="error">{err}</div>}

      <div className="panel" style={{ padding: 0 }}>
        <table className="table">
          <thead>
            <tr>
              <th className="sortable" onClick={() => toggle('releaseTime')}>Fecha{arrow('releaseTime')}</th>
              <th className="sortable" onClick={() => toggle('type')}>Tipo{arrow('type')}</th>
              <th>Estado</th>
              <th>Versión</th>
              <th className="sortable" onClick={() => toggle('releaseUser')}>Autor{arrow('releaseUser')}</th>
              <th>Mensaje</th>
              <th className="sortable num" onClick={() => toggle('fileCount')}>Ficheros{arrow('fileCount')}</th>
              <th className="sortable num" onClick={() => toggle('versionBytes')}>Tamaño{arrow('versionBytes')}</th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((r) => (
              <tr key={r.releaseId}>
                <td className="muted">{formatDate(r.releaseTime)}</td>
                <td>{r.type}</td>
                <td>{r.status ?? '—'}</td>
                <td className="muted">{lastSegment(r.versionName)}</td>
                <td>{r.releaseUserEmail ?? '—'}</td>
                <td>{r.message ?? '—'}</td>
                <td className="num">{r.fileCount ?? '—'}</td>
                <td className="num">{formatBytes(r.versionBytes)}</td>
              </tr>
            ))}
            {sorted.length === 0 && !loading && (
              <tr>
                <td colSpan={8} className="muted" style={{ textAlign: 'center', padding: 32 }}>
                  Sin releases.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {pageToken && (
        <div className="pagination">
          <button onClick={() => loadPage(pageToken, true)} disabled={loading}>
            Cargar más
          </button>
        </div>
      )}
    </div>
  );
}

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleString('es-ES', { dateStyle: 'short', timeStyle: 'short' });
}

function formatBytes(bytes: number | null): string {
  if (bytes === null) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/** Último segmento de un resource name (o '—' si es null). */
function lastSegment(name: string | null): string {
  if (!name) return '—';
  const parts = name.split('/');
  return parts[parts.length - 1];
}
